import { useEffect, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import type { SectionColorRule } from './attribute-colors';
import {
  gridColorRulesAtom,
  sectionGridIdsAtom,
  structureGridCollectionIdAtom,
  wellColorRulesAtom,
  wellFiltersAtom,
  wellResultsRequestedAtom,
  knownGridsAtom,
} from './atoms';
import {
  GRID_COLOR_CATEGORIES,
  GRID_COLOR_TARGETS,
  deriveGridColorValues,
  refreshGridColorRule,
  type GridIdentity,
} from './grid-colors';
import {
  WELL_COLOR_CATEGORIES,
  WELL_COLOR_TARGETS,
  deriveWellColorValues,
  refreshWellColorRule,
} from './well-colors';
import { RailDatasetSection } from './rail-dataset-section';
import { WELL_FILTER_CATEGORIES } from './well-filters';
import { useSectionWells } from './use-section-wells';
import { useStructureGridCollection } from './use-structure-grid-collections';
import { useCrossSection } from './use-cross-section';
import { useCrossSectionRequest } from './use-cross-section-request';

/** Where a new grid rule paints, and what Reset restores it to. */
const GRID_APPLY_TO = { crossSection: true };

/**
 * Where a new well rule paints. Both surfaces by default: the section's wells are the
 * same wells wherever they are drawn, and a rule that coloured only one of the two
 * views would leave the map disagreeing with the plot beside it.
 */
const WELL_APPLY_TO = { crossSection: true, map: true };

/**
 * The rule the section starts with: every grid coloured by its own interval, which is
 * how a stack is read. Editable from the moment it appears, so the default is a
 * starting point rather than a special case.
 */
export const INITIAL_GRID_RULE: SectionColorRule = {
  ruleId: 'grid-color-by-interval',
  attribute: 'geologicInterval',
  selectedValues: [],
  selectedValueLabels: [],
  valueColors: {},
  applyTo: GRID_APPLY_TO,
  enabled: true,
  // Follows the stack until the user narrows it themselves.
  derivedFromData: true,
};

/**
 * The rule the crossings start with: every well coloured by status, in the same
 * colours the map draws well statuses in. Editable like any rule; this is a starting
 * point, not a special case.
 */
export const INITIAL_WELL_RULE: SectionColorRule = {
  ruleId: 'well-color-by-status',
  attribute: 'status',
  selectedValues: [],
  selectedValueLabels: [],
  valueColors: {},
  applyTo: WELL_APPLY_TO,
  enabled: true,
  // Follows the crossings until the user narrows it themselves.
  derivedFromData: true,
};

/**
 * The section's datasets in the display rail: Structure Grids, and the Wells the
 * section crosses. Both follow the rail's own control pattern — an attribute to
 * colour by, the values it derives from what is actually on the section, a swatch per
 * value, and the rule's own state.
 *
 * This is where how it LOOKS is decided. The settings panel decides what the section
 * IS, which is why removing a grid happens there and recolouring one happens here.
 *
 * @param {object} props - The rail's options.
 * @param {boolean} [props.showWells] - False for a host whose own rail colours the
 *   wells (Area Details): Structure Grids only, and no Wells rule seeded.
 * @param {boolean} [props.canAddGridRules] - False for a host whose rail keeps its
 *   one "Color by Attribute" elsewhere: the Geologic Interval rule stays, editable.
 * @return {JSX.Element} The rail's dataset sections.
 */
export const CrossSectionRailSection = ({
  showWells = true,
  canAddGridRules = true,
}: {
  showWells?: boolean;
  canAddGridRules?: boolean;
} = {}): JSX.Element => {
  const [gridRules, setGridRules] = useAtom(gridColorRulesAtom);
  const [wellRules, setWellRules] = useAtom(wellColorRulesAtom);
  const [wellFilters, setWellFilters] = useAtom(wellFiltersAtom);
  const wellsRequested = useAtomValue(wellResultsRequestedAtom);
  const gridIds = useAtomValue(sectionGridIdsAtom);
  const known = useAtomValue(knownGridsAtom);
  const collectionId = useAtomValue(structureGridCollectionIdAtom);
  const { grids } = useStructureGridCollection(collectionId);

  // The crossings the cut returned, read from the same cache the drawer fills.
  const { request, generation } = useCrossSectionRequest();
  const { grouped } = useCrossSection(request, generation);

  // What the section shows: the crossings that survive the rail's filters. The
  // colour rule is derived from these, so the swatches describe what is plotted.
  const wells = useSectionWells(grouped.wells);

  // The stack, in the order the section draws it — which is the order the rule hands
  // out its default colours in.
  const stack = useMemo<GridIdentity[]>(
    () =>
      gridIds.flatMap((id) => {
        // The selected collection first, then whatever has been learned elsewhere: a
        // grid the section crosses may belong to another collection entirely, and it
        // still has an interval to be coloured by.
        const grid =
          grids.find((candidate) => candidate.structureGridId === id) ??
          known[String(id)];
        if (!grid) {
          return [];
        }
        return [
          {
            gridId: String(id),
            geologicInterval: grid.geologicInterval,
            gridName: grid.gridSurface,
            dataSource: grid.source,
            dataOwner: grid.owner,
            displayColor: grid.geologicIntervalDisplayColor,
          },
        ];
      }),
    [gridIds, grids, known]
  );

  // Adding or removing a grid re-derives the rule's values, keeping the swatches the
  // user has edited and handing defaults to values not seen before.
  useEffect(() => {
    if (stack.length === 0) {
      return;
    }
    setGridRules((current) => {
      const seeded = current.length === 0 ? [INITIAL_GRID_RULE] : current;
      const refreshed = seeded.map((rule) => refreshGridColorRule(rule, stack));
      // Same values and same colours means nothing to write — otherwise this effect
      // would loop on its own output.
      const unchanged =
        refreshed.length === current.length &&
        refreshed.every((rule, index) => {
          const before = current[index];
          return (
            before &&
            before.selectedValues.join() === rule.selectedValues.join() &&
            JSON.stringify(before.valueColors) ===
              JSON.stringify(rule.valueColors)
          );
        });
      return unchanged ? current : refreshed;
    });
  }, [stack, setGridRules]);

  // The same for wells, against the crossings the cut came back with.
  useEffect(() => {
    if (!showWells || wells.length === 0) {
      return;
    }
    setWellRules((current) => {
      const seeded = current.length === 0 ? [INITIAL_WELL_RULE] : current;
      const refreshed = seeded.map((rule) => refreshWellColorRule(rule, wells));
      const unchanged = refreshed.every((rule, index) => {
        const before = current[index];
        return (
          before &&
          before.selectedValues.join() === rule.selectedValues.join() &&
          JSON.stringify(before.valueColors) ===
            JSON.stringify(rule.valueColors)
        );
      });
      return unchanged ? current : refreshed;
    });
  }, [showWells, wells, setWellRules]);

  const gridOptions = useMemo(
    () =>
      Object.fromEntries(
        GRID_COLOR_CATEGORIES.map((category) => [
          category.id,
          deriveGridColorValues(stack, category.id).map((value) => ({
            id: value,
            name: value,
          })),
        ])
      ),
    [stack]
  );

  const wellOptions = useMemo(
    () =>
      Object.fromEntries(
        WELL_COLOR_CATEGORIES.map((category) => [
          category.id,
          deriveWellColorValues(wells, category.id).map((value) => ({
            id: value,
            name: value,
          })),
        ])
      ),
    [wells]
  );

  // Filter options come from every crossing, not the surviving ones: options taken
  // from filtered data would drop the values a filter excludes, leaving no way to
  // widen it again.
  const wellFilterOptions = useMemo(
    () =>
      Object.fromEntries(
        WELL_FILTER_CATEGORIES.map((category) => [
          category.id,
          deriveWellColorValues(grouped.wells, category.id).map((value) => ({
            id: value,
            name: value,
          })),
        ])
      ),
    [grouped.wells]
  );

  return (
    <>
      <RailDatasetSection
        title="Structure Grids"
        count={stack.length}
        hasItems={stack.length > 0}
        emptyMessage="No grids on the section yet. Add one from Cross Section Settings."
        rules={gridRules}
        onRulesChange={setGridRules}
        categories={GRID_COLOR_CATEGORIES}
        targets={GRID_COLOR_TARGETS}
        getOptions={(categoryId) => gridOptions[categoryId] ?? []}
        refresh={(rule) => refreshGridColorRule(rule, stack)}
        applyTo={GRID_APPLY_TO}
        canAddRules={canAddGridRules}
      />
      {showWells && (
        <RailDatasetSection
          title="Wells"
          count={wells.length}
          hasItems={wells.length > 0}
          // The crossings are a calculated result, asked for from the header. Before the
          // ask the rail says where they come from rather than looking broken; after an
          // ask that found nothing it says so, rather than repeating the instruction.
          emptyMessage={
            wellsRequested
              ? 'No wells cross this section.'
              : 'No well crossings yet. Calculate well intersections from the header to colour them.'
          }
          rules={wellRules}
          onRulesChange={setWellRules}
          categories={WELL_COLOR_CATEGORIES}
          targets={WELL_COLOR_TARGETS}
          getOptions={(categoryId) => wellOptions[categoryId] ?? []}
          refresh={(rule) => refreshWellColorRule(rule, wells)}
          applyTo={WELL_APPLY_TO}
          filters={wellFilters}
          onFiltersChange={setWellFilters}
          getFilterOptions={(categoryId) => wellFilterOptions[categoryId] ?? []}
        />
      )}
    </>
  );
};
